import { connect } from 'react-redux';
import i18next from 'i18next';
import React, { useEffect, useMemo, useState } from 'react';

import { closeGenericModal, openGenericModal, refreshGenericModal } from '@actions/modal';
import { loading, loadingSuccess } from '@actions/loading';
import { showErrorMessage } from '@actions/messageconfirmation';

import { Button } from '@commons/utils/styledLibraryComponents';
import { generateUUID } from '@commons/utils/uuid';
import { getFilteredStoresWithSelectedGroups } from '@commons/ExportModalWithStoresAndPeriod/utils';

import { getClientInfo } from '@selectors/client';
import { getHasRecipeVariants } from '@selectors/featureProps';

import { generatePDFBlob } from '@admin/products/recipes/components/ExportRecipeWithCompositionAndPreparation';
import { getRecipeExportName } from '@admin/products/recipes/utils/downloadRecipe';

import { computeInitialSelections } from './components/RecipeVariantModal/utils/computeInitialSelections';
import { computeStoresWithVariantLink } from './components/RecipeVariantModal/utils/computeStoresWithVariantLink';
import { getDeleteRecipeVariantModalParams } from '../../utils/modalConfigurations';
import { getReassignmentConfirmationModalConfig } from './components/RecipeVariantModal/utils/getReassignmentConfirmationModalConfig';
import { getRecipeVariantModalConfig } from './components/RecipeVariantModal/utils/getRecipeVariantModalConfig';
import { getSalesPointStores } from '@selectors/stores';
import { VARIANT_MODAL_MODE } from './components/RecipeVariantModal/constants';

const RecipeDetailActions = ({
  recipe,
  compositionSettings,
  client,
  hasVariants,
  isKitchen,
  stores,
  recipeVariants,
  setRecipeVariants,
  setDeletedVariantIds,
  selectedVariantId,
  setSelectedVariantId,
  modal,
  openGenericModal,
  closeGenericModal,
  refreshGenericModal,
  showErrorMessage,
  pageLoading,
  pageLoaded,
}) => {
  const selectedVariant = recipeVariants.find(({ id }) => id === selectedVariantId);

  const [variantModalMode, setVariantModalMode] = useState(null);
  const [variantName, setVariantName] = useState('');
  const [variantGroups, setVariantGroups] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [variantStores, setVariantStores] = useState([]);
  const [selectedStores, setSelectedStores] = useState([]);
  const [isReassignmentConfirmationOpen, setIsReassignmentConfirmationOpen] = useState(false);

  const isDownloadDisabled =
    !compositionSettings.length ||
    !recipe.name ||
    !recipe.value ||
    !recipe.category ||
    !recipe.unit;

  const getVariantNameErrorMessage = (name, mode) => {
    if (mode === VARIANT_MODAL_MODE.MAIN_RECIPE) {
      return null;
    }

    const trimmedName = name.trim();

    if (!trimmedName) {
      return null;
    }

    const duplicatedVariant = recipeVariants.find((variant) => {
      if (mode === VARIANT_MODAL_MODE.EDIT && variant.id === selectedVariantId) {
        return false;
      }

      return variant.name.trim().toLowerCase() === trimmedName.toLowerCase();
    });

    if (duplicatedVariant) {
      return i18next.t('ADMIN.RECIPES.VARIANT_MODAL.NAME_DUPLICATE_ERROR');
    }

    return null;
  };

  const handleVariantModalCloseCleanUp = () => {
    setVariantModalMode(null);
    setVariantName('');
    setVariantGroups([]);
    setSelectedGroups([]);
    setVariantStores([]);
    setSelectedStores([]);
    setIsReassignmentConfirmationOpen(false);
  };

  /**
   * Applies a variant create/edit to the local state. New variants get a temporary id and an
   * `isNew` flag so the save payload omits their id (= a create on the back). A store can only
   * belong to a single variant of a recipe, so any store newly claimed here is stripped from the
   * other variants; stores left out of every variant fall back to the main recipe implicitly.
   */
  const handleVariantModalConfirmedSave = () => {
    const trimmedName = variantName.trim();
    const newStoreIds = selectedStores.map(({ id }) => id);
    const newStoreIdsSet = new Set(newStoreIds);

    const stripClaimedStores = (variant) => ({
      ...variant,
      storeIds: variant.storeIds.filter((storeId) => !newStoreIdsSet.has(storeId)),
    });

    if (variantModalMode === VARIANT_MODAL_MODE.CREATE) {
      const compositionToDuplicate = selectedVariant?.composition ?? compositionSettings;

      const newVariant = {
        id: generateUUID(),
        name: trimmedName,
        storeIds: newStoreIds,
        composition: compositionToDuplicate,
        isNew: true,
      };

      setRecipeVariants([...recipeVariants.map(stripClaimedStores), newVariant]);
      setSelectedVariantId(newVariant.id);
    } else {
      setRecipeVariants(
        recipeVariants.map((variant) => {
          if (variant.id === selectedVariantId) {
            return { ...variant, name: trimmedName, storeIds: newStoreIds };
          }

          return stripClaimedStores(variant);
        }),
      );
    }

    handleVariantModalCloseCleanUp();
    closeGenericModal();
  };

  const handleReassignmentConfirmationClose = () => {
    setIsReassignmentConfirmationOpen(false);

    setTimeout(() => {
      openGenericModal(getCurrentVariantModalConfig());
    });
  };

  const handleVariantModalSave = () => {
    const hasReassignedStores = selectedStores.some((store) => store.isLinkedToOtherVariant);

    if (hasReassignedStores) {
      setIsReassignmentConfirmationOpen(true);
      openGenericModal(
        getReassignmentConfirmationModalConfig({
          handleCloseCleanUp: handleReassignmentConfirmationClose,
          handleConfirm: handleVariantModalConfirmedSave,
        }),
      );
      return;
    }

    handleVariantModalConfirmedSave();
  };

  const getCurrentVariantModalConfig = ({
    mode = variantModalMode,
    name = variantName,
    groups = variantGroups,
    groupsSelection = selectedGroups,
    storeChoices = variantStores,
    storesSelection = selectedStores,
  } = {}) =>
    getRecipeVariantModalConfig({
      mode,
      variantName: name,
      setVariantName,
      isVariantNameLocked:
        mode === VARIANT_MODAL_MODE.EDIT && name === i18next.t('ADMIN.RECIPES.MAIN_RECIPE_TAB'),
      nameErrorMessage: getVariantNameErrorMessage(name, mode),
      groups,
      selectedGroups: groupsSelection,
      setSelectedGroups,
      stores: storeChoices,
      selectedStores: storesSelection,
      setSelectedStores,
      handleSave: handleVariantModalSave,
      handleCloseCleanUp: handleVariantModalCloseCleanUp,
    });

  const salesPointStores = useMemo(() => getSalesPointStores(stores), [stores]);

  const getStoresMatchingSelectedGroups = (groupsSelection) => {
    if (!groupsSelection.length) {
      return salesPointStores;
    }

    return getFilteredStoresWithSelectedGroups(groupsSelection, salesPointStores);
  };

  useEffect(() => {
    if (!variantModalMode) {
      return;
    }

    const isMainRecipeMode = variantModalMode === VARIANT_MODAL_MODE.MAIN_RECIPE;
    const currentVariantId =
      variantModalMode === VARIANT_MODAL_MODE.EDIT ? selectedVariantId : null;
    const filteredStores = getStoresMatchingSelectedGroups(selectedGroups);
    const storesWithVariantLink = computeStoresWithVariantLink(
      filteredStores,
      recipeVariants,
      currentVariantId,
      { disableUnlinkedStores: isMainRecipeMode },
    );
    const displayedStoreIds = new Set(storesWithVariantLink.map(({ id }) => id));

    setVariantStores(storesWithVariantLink);
    setSelectedStores((currentSelectedStores) =>
      currentSelectedStores.filter(({ id }) => displayedStoreIds.has(id)),
    );
  }, [selectedGroups, variantModalMode, salesPointStores, recipeVariants, selectedVariantId]);

  useEffect(() => {
    if (!variantModalMode || !modal.GenericModalBool || isReassignmentConfirmationOpen) {
      return;
    }

    refreshGenericModal(getCurrentVariantModalConfig());
  }, [
    variantModalMode,
    variantName,
    variantGroups,
    selectedGroups,
    variantStores,
    selectedStores,
    recipeVariants,
    selectedVariantId,
    modal.GenericModalBool,
    isReassignmentConfirmationOpen,
  ]);

  const handleDeleteVariant = () => {
    // Only persisted variants need to be reported to the back for deletion; a variant created in
    // this session (never saved) simply disappears from local state. Its stores revert to the
    // main recipe implicitly (any store not claimed by a variant belongs to the main recipe).
    if (selectedVariant && !selectedVariant.isNew) {
      setDeletedVariantIds((currentDeletedVariantIds) => [
        ...currentDeletedVariantIds,
        selectedVariant.id,
      ]);
    }

    setRecipeVariants(recipeVariants.filter(({ id }) => id !== selectedVariantId));
    setSelectedVariantId(null);
    closeGenericModal();
  };

  const openDeleteVariantModal = () => {
    openGenericModal(getDeleteRecipeVariantModalParams(selectedVariant.name, handleDeleteVariant));
  };

  const getInitialVariantName = (mode) => {
    if (mode === VARIANT_MODAL_MODE.MAIN_RECIPE) {
      return recipe.name ?? '';
    }

    if (mode === VARIANT_MODAL_MODE.EDIT) {
      return selectedVariant?.name ?? '';
    }

    return '';
  };

  const openVariantModal = (mode) => {
    const isMainRecipeMode = mode === VARIANT_MODAL_MODE.MAIN_RECIPE;
    const initialVariantName = getInitialVariantName(mode);
    const currentVariantId = mode === VARIANT_MODAL_MODE.EDIT ? selectedVariantId : null;
    const initialSelections = computeInitialSelections({
      mode,
      stores: salesPointStores,
      recipeVariants,
      selectedVariant,
    });
    const filteredStores = getStoresMatchingSelectedGroups(initialSelections.selectedGroups);
    const storesWithVariantLink = computeStoresWithVariantLink(
      filteredStores,
      recipeVariants,
      currentVariantId,
      { disableUnlinkedStores: isMainRecipeMode },
    );
    const selectedStoresWithVariantLink = computeStoresWithVariantLink(
      initialSelections.selectedStores,
      recipeVariants,
      currentVariantId,
      { disableUnlinkedStores: isMainRecipeMode },
    );

    setVariantModalMode(mode);
    setVariantName(initialVariantName);
    setVariantGroups(initialSelections.groups);
    setSelectedGroups(initialSelections.selectedGroups);
    setVariantStores(storesWithVariantLink);
    setSelectedStores(selectedStoresWithVariantLink);

    openGenericModal(
      getCurrentVariantModalConfig({
        mode,
        name: initialVariantName,
        groups: initialSelections.groups,
        groupsSelection: initialSelections.selectedGroups,
        storeChoices: storesWithVariantLink,
        storesSelection: selectedStoresWithVariantLink,
      }),
    );
  };

  // A variant duplicates a composition, so creating one only makes sense once the base recipe
  // has at least one component.
  const isCreateVariantDisabled = !compositionSettings.length;

  // Central kitchen recipes cannot have variants: their save does not persist them
  const canManageVariants = hasVariants && !isKitchen;

  const createVariantAction = canManageVariants
    ? [
        {
          label: i18next.t('ADMIN.RECIPES.VARIANT_MODAL.CREATE_ACTION'),
          icon: isCreateVariantDisabled
            ? '/images/inpulse/add-lmgrey-small.svg'
            : '/images/inpulse/add-black-small.svg',
          isDisabled: isCreateVariantDisabled,
          handleClick: () => {
            openVariantModal(VARIANT_MODAL_MODE.CREATE);
          },
        },
      ]
    : [];

  const isMainRecipeSelected = selectedVariantId === null;
  const manageMainRecipeAction =
    canManageVariants && isMainRecipeSelected && recipeVariants.length > 0
      ? [
          {
            label: i18next.t('ADMIN.RECIPES.VARIANT_MODAL.EDIT_ACTION'),
            icon: '/images/inpulse/gear-black-small.svg',
            handleClick: () => {
              openVariantModal(VARIANT_MODAL_MODE.MAIN_RECIPE);
            },
          },
        ]
      : [];

  const editVariantActions = selectedVariant
    ? [
        {
          label: i18next.t('ADMIN.RECIPES.VARIANT_MODAL.EDIT_ACTION'),
          icon: '/images/inpulse/gear-black-small.svg',
          handleClick: () => {
            openVariantModal(VARIANT_MODAL_MODE.EDIT);
          },
        },
        {
          label: i18next.t('ADMIN.RECIPES.DELETE_VARIANT_ACTION'),
          icon: '/images/inpulse/trash-black-small.svg',
          handleClick: openDeleteVariantModal,
        },
      ]
    : [];

  const downloadAction = {
    isDisabled: isDownloadDisabled,
    label: i18next.t('ADMIN.RECIPES.DOWNLOAD'),
    icon: isDownloadDisabled
      ? '/images/inpulse/file-download-lmgrey-small.svg'
      : '/images/inpulse/file-download-black-small.svg',
    handleClick: () => {
      generatePDFBlob({
        recipe: {
          ...recipe,
          name: getRecipeExportName({ recipe, selectedVariantId, recipeVariants }),
        },
        compositionSettings,
        client,
        showErrorMessage,
        pageLoading,
        pageLoaded,
      });
    },
  };

  return (
    <Button
      actions={[
        ...createVariantAction,
        ...manageMainRecipeAction,
        ...editVariantActions,
        downloadAction,
      ]}
      color={'inpulse-outline'}
      iconDropdown={'/images/inpulse/carret-black.svg'}
      iconOnLeft={false}
      label={i18next.t('ADMIN.RECIPES.ITEM_LIST_BUTTONS_ACTIONS')}
    />
  );
};

const mapStateToProps = (state) => ({
  client: getClientInfo(state.baseReducer.user),
  hasVariants: getHasRecipeVariants(state.baseReducer.userRights),
  modal: state.modalReducer,
  stores: state.baseReducer.activeStores,
});

const mapDispatchToProps = (dispatch) => ({
  openGenericModal: (params) => {
    dispatch(openGenericModal(params));
  },
  closeGenericModal: () => {
    dispatch(closeGenericModal());
  },
  refreshGenericModal: (params) => {
    dispatch(refreshGenericModal(params));
  },
  showErrorMessage: (message) => {
    dispatch(showErrorMessage(message));
  },
  pageLoading: () => {
    dispatch(loading());
  },
  pageLoaded: () => {
    dispatch(loadingSuccess());
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(RecipeDetailActions);
